import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowsLeftRight, CheckCircle, Clock, XCircle, User as UserIcon } from "@phosphor-icons/react";
import { formatBRL, formatDate, recurrenceLabel } from "@/lib/format";

const FILTERS = [
  { key: "all", label: "Todas" },
  { key: "pending", label: "Pendentes" },
  { key: "approved", label: "Aprovadas" },
  { key: "cancelled", label: "Canceladas" },
];

const STATUS_META = {
  pending:   { label: "Pendente",  icon: Clock,       color: "amber" },
  approved:  { label: "Aprovada",  icon: CheckCircle, color: "emerald" },
  cancelled: { label: "Cancelada", icon: XCircle,     color: "rose" },
  rejected:  { label: "Rejeitada", icon: XCircle,     color: "rose" },
  expired:   { label: "Expirada",  icon: XCircle,     color: "zinc" },
};

const PlanChanges = () => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/plan-changes", {
        params: filter !== "all" ? { status_filter: filter } : {},
      });
      setItems(data);
    } catch (err) {
      const code = err?.response?.status;
      if (code === 404 || code === 405) {
        // backend in production may not have this endpoint yet
        setItems([]);
      }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filter]);

  return (
    <div>
      <div className="mb-6">
        <p className="text-xs tracking-[0.3em] uppercase text-zinc-500 font-semibold">Auditoria</p>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Trocas de plano</h1>
        <p className="text-sm text-zinc-500 mt-1">Solicitações de troca feitas pelos clientes via dashboard.</p>
      </div>

      <Tabs value={filter} onValueChange={setFilter} className="mb-4">
        <TabsList>
          {FILTERS.map((f) => (
            <TabsTrigger key={f.key} value={f.key} data-testid={`pc-filter-${f.key}`}>{f.label}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {loading ? (
        <Card className="border-zinc-200 p-10 text-center text-zinc-500 text-sm">Carregando...</Card>
      ) : items.length === 0 ? (
        <Card className="border-dashed border-zinc-300 p-10 text-center text-zinc-500">
          <ArrowsLeftRight size={28} weight="duotone" className="mx-auto mb-2 text-zinc-400"/>
          Nenhuma solicitação encontrada com este filtro.
        </Card>
      ) : (
        <div className="space-y-3" data-testid="pc-list">
          {items.map((r) => {
            const meta = STATUS_META[r.status] || STATUS_META.pending;
            const Icon = meta.icon;
            return (
              <Card key={r.id} className={`border-zinc-200 p-5 card-soft`} data-testid={`pc-row-${r.id}`}>
                <div className="flex flex-wrap items-start gap-4 justify-between">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className={`w-10 h-10 rounded-xl bg-${meta.color}-50 text-${meta.color}-700 grid place-items-center flex-shrink-0`}>
                      <Icon size={20} weight="fill"/>
                    </div>
                    <div className="min-w-0">
                      <div className="font-display text-base font-semibold flex items-center gap-2 flex-wrap">
                        {r.user_name}
                        {r.is_subaccount && (
                          <span className="px-1.5 py-0.5 rounded text-[10px] tracking-widest uppercase font-semibold bg-zinc-100 border border-zinc-200 text-zinc-600">
                            Subconta{r.beneficiary && ` · ${r.beneficiary}`}
                          </span>
                        )}
                        {!r.is_subaccount && (
                          <span className="px-1.5 py-0.5 rounded text-[10px] tracking-widest uppercase font-semibold bg-amber-50 border border-amber-200 text-amber-700">
                            Principal
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-zinc-500">@{r.username}</div>
                      <div className="text-xs text-zinc-600 mt-2 flex items-center gap-1.5 flex-wrap">
                        <UserIcon size={12} weight="duotone"/> Pagador: <strong>{r.requested_by_name}</strong>
                      </div>
                    </div>
                  </div>
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-widest bg-${meta.color}-50 text-${meta.color}-700 border border-${meta.color}-200`}>
                    <Icon size={12} weight="fill"/> {meta.label}
                  </span>
                </div>

                <div className="grid sm:grid-cols-4 gap-3 mt-4 text-sm">
                  <Stat label="Plano anterior" value={recurrenceLabel[r.current_recurrence] || r.current_recurrence || "—"} sub={r.current_plan_value ? formatBRL(r.current_plan_value) : ""}/>
                  <Stat label="Novo plano" value={`${r.new_plan_name} · ${recurrenceLabel[r.new_recurrence] || r.new_recurrence}`} sub={formatBRL(r.amount)} accent/>
                  <Stat label="Desconto aplicado" value={`${r.discount_pct || 0}%`} sub={r.loyalty_tier?.toUpperCase()}/>
                  <Stat label={r.applied ? "Novo vencimento" : "Vencimento previsto"} value={formatDate(r.applied ? r.new_due_date : r.projected_new_due_date)} sub={r.applied ? "Aplicado" : "Aguardando aprovação"}/>
                </div>

                <div className="mt-3 pt-3 border-t border-zinc-100 flex flex-wrap items-center gap-3 text-[11px] text-zinc-500">
                  <span>{formatDate(r.created_at)} {new Date(r.created_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</span>
                  <span>·</span>
                  <span>Forma: <strong className="text-zinc-700 uppercase">{r.method}</strong></span>
                  {r.mp_payment_id && <><span>·</span><span>MP: <code className="font-mono text-[10px]">{r.mp_payment_id}</code></span></>}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

const Stat = ({ label, value, sub, accent }) => (
  <div>
    <div className="text-[10px] tracking-widest uppercase text-zinc-500 font-semibold">{label}</div>
    <div className={`font-display text-sm font-semibold mt-0.5 ${accent ? "text-blue-700" : ""}`}>{value}</div>
    {sub && <div className="text-[11px] text-zinc-500 mt-0.5">{sub}</div>}
  </div>
);

export default PlanChanges;
